import apiV2 from "api-v2";
import type {
	ScheduleOfRatesData,
	ScheduleColumn,
	ScheduleRow,
	ScheduleImportPreview,
	ScheduleColumnType,
} from "../components/QuoteTable/schedule-of-rates.types";

/* -------------------------------------------------------------------------- */
/* Backend ↔ Frontend mapping                                                 */
/* -------------------------------------------------------------------------- */

interface ApiColumn {
	id?: number;
	name: string;
	type: ScheduleColumnType;
	sort_order: number;
}

interface ApiRow {
	id?: number;
	cells: Record<number | string, string>;
	sort_order: number;
}

interface ApiScheduleResponse {
	id: number;
	columns: ApiColumn[];
	rows: ApiRow[];
	revision?: number;
	is_submitted?: boolean;
}

const mapColumnFromApi = (col: ApiColumn): ScheduleColumn => ({
	id: col.sort_order,
	label: col.name,
	columnType: col.type,
	sortOrder: col.sort_order,
});

const mapRowFromApi = (row: ApiRow, index: number): ScheduleRow => ({
	id: row.id ?? index,
	sortOrder: row.sort_order,
	values: row.cells ?? {},
});

const mapResponseFromApi = (data: ApiScheduleResponse | null): ScheduleOfRatesData | null => {
	if (!data) {
		return null;
	}
	return {
		id: data.id,
		columns: (data.columns ?? []).map(mapColumnFromApi),
		rows: (data.rows ?? []).map(mapRowFromApi),
		revision: data.revision,
		isSubmitted: data.is_submitted,
	};
};

/* -------------------------------------------------------------------------- */
/* Payload type                                                               */
/* -------------------------------------------------------------------------- */

export interface SaveSchedulePayload {
	name: string;
	columns: {
		name: string;
		type: ScheduleColumnType;
		sort_order: number;
	}[];
	rows: {
		sort_order: number;
		cells: Record<number, string>;
	}[];
}

/* -------------------------------------------------------------------------- */
/* Submission-level                                                           */
/* -------------------------------------------------------------------------- */

export const getScheduleOfRates = async (
	inviteId: number
): Promise<ScheduleOfRatesData | null> => {
	const response = await apiV2.get<{ data: ApiScheduleResponse | null }>(
		`rfx-invite/${inviteId}/schedule-of-rates`
	);
	return mapResponseFromApi(response.data.data);
};

export const saveScheduleOfRates = async (
	inviteId: number,
	payload: SaveSchedulePayload
): Promise<ScheduleOfRatesData> => {
	const response = await apiV2.post<{ data: ApiScheduleResponse }>(
		`rfx-invite/${inviteId}/schedule-of-rates`,
		payload
	);
	return mapResponseFromApi(response.data.data)!;
};

export const deleteScheduleOfRates = async (
	inviteId: number,
	scheduleId: number
): Promise<void> => {
	await apiV2.delete(`rfx-invite/${inviteId}/schedule-of-rates/${scheduleId}`);
};

export const initScheduleFromTemplate = async (
	inviteId: number
): Promise<ScheduleOfRatesData | null> => {
	try {
		const response = await apiV2.post<{ data: ApiScheduleResponse | null }>(
			`rfx-invite/${inviteId}/schedule-of-rates/init`,
			undefined,
			{ expectedErrors: [404] } as Record<string, unknown>
		);
		return mapResponseFromApi(response.data.data);
	} catch (err: unknown) {
		// No default template configured — return null so the component shows an empty editor
		const status = (err as { response?: { status?: number } })?.response?.status;
		if (status === 404) {
			return null;
		}
		throw err;
	}
};

export const importScheduleFromExcel = async (
	inviteId: number,
	file: File
): Promise<ScheduleImportPreview> => {
	const formData = new FormData();
	formData.append("file", file);
	const response = await apiV2.post<{ data: ScheduleImportPreview }>(
		`rfx-invite/${inviteId}/schedule-of-rates/import`,
		formData,
		{ headers: { "Content-Type": "multipart/form-data" } }
	);
	return response.data.data;
};

/* -------------------------------------------------------------------------- */
/* Company-level                                                              */
/* -------------------------------------------------------------------------- */

export const getCompanyScheduleOfRates = async (
	_companyId: number
): Promise<ScheduleOfRatesData | null> => {
	const response = await apiV2.get<{ data: ApiScheduleResponse | null }>(
		`rfx-settings/schedule-of-rates`
	);
	return mapResponseFromApi(response.data.data);
};

export const saveCompanyScheduleOfRates = async (
	_companyId: number,
	payload: SaveSchedulePayload
): Promise<ScheduleOfRatesData> => {
	const response = await apiV2.post<{ data: ApiScheduleResponse }>(
		`rfx-settings/schedule-of-rates`,
		payload
	);
	return mapResponseFromApi(response.data.data)!;
};
